import React, {
    DragEvent as ReactDragEvent, MouseEvent as ReactMouseEvent, RefObject, useEffect, useState,
} from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { setDrag } from '../../reducers/drag/dragReducerActions';
import { MapImage, MapShape, Token } from '../../reducers/map/mapReducer';
import EditTokenModal from '../Token/EditTokenModal';
import { useSelector } from 'react-redux';
import { Rootstate } from '../../store';
import { setSelectedElement, setTokens } from '../../reducers/map/mapReducerActions';
import { setMeasureState } from '../../reducers/measure/measureReducerActions';
import Client from '../../utils/network';
import { updatePartialMapOnServer } from '../../utils/updatePartialMapOnServer';

interface TokenMouseEvent {
    type: 'token',
    token: Token,
}

interface SpellMouseEvent {
    type: 'spell',
    spell: MapShape,
}

interface MapImageMouseEvent {
    type: 'image',
    image: MapImage,
}

interface GridMouseEvent {
    type: 'grid',
}

type MapMouseEvent = TokenMouseEvent | SpellMouseEvent | MapImageMouseEvent | GridMouseEvent;


interface UseClickManagerProps {
    gridRef: RefObject<HTMLDivElement>
}

const useClickManager = ({ gridRef }: UseClickManagerProps) => {

    const dispatch = useDispatch();

    const {
        tokens,
        mapId,
        mode,
        zoom,
        selected,
        guildId,
        channelId,
    } = useSelector((state: Rootstate) => ({
        ...state.grid,
        tokens: state.map.map?.tokenLayer,
        spells: state.map.map?.spellLayer,
        images: state.map.map?.objectLayer,
        mapId: state.map.mapId,
        mode: state.map.mode,
        zoom: state.zoom.zoom,
        selected: state.map.selectedElement,
        guildId: state.auth.guildId,
        channelId: state.auth.channelId,
    }), shallowEqual);

    const [showModal, setShowModal] = useState(false);
    const [editingToken, setEditingToken] = useState<Token>();
    const [editingTokenCopy, setEditingTokenCopy] = useState<Token>();

    useEffect(() => {
        if(selected) {
            const onKeyDown = (e: KeyboardEvent) => {
                if(e.code === 'Escape') {
                    dispatch(setSelectedElement(undefined));
                }
            };

            window.addEventListener('keydown', onKeyDown);
            return () => {
                window.removeEventListener('keydown', onKeyDown);
            };
        }
    }, [selected]);

    const handlePing = (e: ReactMouseEvent<Element>) => {
        if(!guildId || !channelId) {
            return;
        }
        const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
        const mappedX = (e.clientX - x) / zoom;
        const mappedY = (e.clientY - y) / zoom;
        Client.request(
            `/api/map/${guildId}/${channelId}/ping`,
            {
                method: 'POST',
                body: JSON.stringify({
                    x: mappedX,
                    y: mappedY,
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        ).then(() => {
            return;
        }).catch(() => {
            // eslint-disable-next-line no-console
            console.log('error');
        });
    };

    const handleClick  = (e: ReactMouseEvent<Element>, event: MapMouseEvent) => {
        if(e.ctrlKey) {
            return;
        }
        if(e.shiftKey) {
            handlePing(e);
            return;
        }
        switch(event.type) {
        case 'token': {
            e.stopPropagation();
            const tokenId = event.token._id;
            dispatch(setSelectedElement(tokenId ? { _id: tokenId, type: 'token' } : undefined));
            break;
        }
        case 'spell': {
            e.stopPropagation();
            const spellId = event.spell._id;
            dispatch(setSelectedElement(spellId ? { _id: spellId, type: 'spell'} : undefined));
            break;
        }
        case 'image': {
            e.stopPropagation();
            dispatch(
                setSelectedElement(
                    (event.image._id)
                        ? { _id: event.image._id, type: 'image' }
                        : undefined,
                ),
            );
            break;
        }
        case 'grid':
            if(selected) {
                dispatch(setSelectedElement(undefined));
            }
            break;
        }
    };

    const handleDrag = (e: ReactDragEvent<HTMLElement>, dragEvent: MapMouseEvent) => {
        e.preventDefault();
        if(e.ctrlKey) {
            return;
        }
        switch (mode) {
        case 'default':
            handleElementDrag(e, dragEvent);
            return;
        case 'measure': {
            e.stopPropagation();
            const { x = 0, y = 0 } = gridRef.current?.getBoundingClientRect() ?? {};
            dispatch(setMeasureState({
                measuring: true,
                measureStart: {
                    x: (e.clientX - x) / zoom,
                    y: (e.clientY - y) / zoom,
                },
            }));
            const handleMeasureEnd = () => {
                dispatch(setMeasureState({ measuring: false, measureStart: { x: 0, y: 0 }}));
                window.removeEventListener('mouseup', handleMeasureEnd);
            };
            const preventClickDefault = (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                window.removeEventListener('click', preventClickDefault, true);
            };
            window.addEventListener('click', preventClickDefault, true);
            window.addEventListener('mouseup', handleMeasureEnd);
        }
        }
    };

    const handleElementDrag = (e: ReactDragEvent<HTMLElement>, event: MapMouseEvent) => {
        const prepElementDrag = () => {
            const target = e.currentTarget;
            const currentOpacity = target.style.opacity;
            target.style.opacity = '0';
            target.style.pointerEvents = 'none';
            const handleDragEnd = (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                dispatch(setDrag(false));
                target.style.opacity = currentOpacity;
                target.style.pointerEvents = 'all';
                window.removeEventListener('mouseup', handleDragEnd);
            };
            const preventClickDefault = (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                window.removeEventListener('click', preventClickDefault, true);
            };
            window.addEventListener('click', preventClickDefault, true);
            window.addEventListener('mouseup', handleDragEnd);
            e.stopPropagation();
        };

        switch(event.type) {
        case 'token':
            prepElementDrag();
            handleTokenDrag(e as ReactDragEvent<HTMLImageElement>, event.token);
            break;
        case 'spell':
            prepElementDrag();
            handleSpellDrag(e as ReactDragEvent<HTMLImageElement>, event.spell);
            break;
        case 'image':
            prepElementDrag();
            handleImageDrag(e as ReactDragEvent<HTMLImageElement>, event.image);
            break;
        default:
            // do nothing
        }
    };

    const handleTokenDrag = (e: ReactDragEvent<HTMLImageElement>, el: Token) => {
        dispatch(setDrag(
            true,
            {
                type: 'move-token',
                details: {
                    token: el,
                    startX: e.clientX,
                    startY: e.clientY,
                    rotate: 0,
                },
            },
        ));
    };

    const handleSpellDrag = (e: ReactDragEvent<HTMLDivElement>, el: MapShape) => {
        dispatch(setDrag(
            true,
            {
                type: 'move-spell',
                details: {
                    shape: el,
                    startX: e.clientX,
                    startY: e.clientY,
                    rotate: 0,
                },
            },
        ));
    };

    const handleImageDrag = (e: ReactDragEvent<HTMLImageElement>, el: MapImage) => {
        dispatch(setDrag(
            true,
            {
                type: 'move-map-image',
                details: {
                    image: el,
                    startX: e.clientX,
                    startY: e.clientY,
                    rotate: el.rotation ?? 0,
                },
            },
        ));
    };

    const children = (
        <>
            <EditTokenModal
                show={showModal && !!editingToken}
                toggle={() => setShowModal(false)}
                token={editingTokenCopy as Token}
                onChange={(val) => setEditingTokenCopy(val)}
                onSubmit={() => {
                    const newTokens = (tokens ?? []).filter((token) => token !== editingToken);
                    newTokens.push(editingTokenCopy as Token);
                    dispatch(setTokens(newTokens));
                    updatePartialMapOnServer({ tokenLayer: newTokens }, mapId);
                    setShowModal(false);
                }}
                onCancel={() => {
                    setShowModal(false);
                    setEditingToken(undefined);
                }}
            />
        </>
    );

    return {
        handleDrag,
        handleClick,
        children,
    };
};

export default useClickManager;
